import { computed, ref, watch, watchEffect } from "vue";
import { showAlert } from "@allwyn/design-system/src/modal-alert/show-alert";
import { useCookieStorage, useLocalStorage } from "@allwyn/storage/composables/use-storage";
import { usePageVisibility } from "@allwyn/utils/composables/use-page-visibility";
import { DateTimeUtils } from "@allwyn/utils/date-time-utils";
import { NgBalance, NgRestoreSession, NgSession } from "@allwyn/utils/models/ng-fw";
import { WECOMA_CONSTANTS } from "@allwyn/utils/wecoma/wecoma-constants";
import { getNgTimeDuration } from "./ng-integration";
import { ngSessionToContact } from "./ng-session-to-contact";
import { logLogin } from "./utils/log-login";
import {
    clearPendingWebViewRestore,
    getPendingWebViewRestore,
    persistPendingWebViewRestore,
    registerFailedWebViewRestore,
} from "./utils/webview-session-restore";

const storage = useCookieStorage();

const session = ref<NgSession | null>(null);
const balance = ref<NgBalance | null>(null);
const logonDate = ref<number | null>(null);
const showBalance = ref(storage.getItem("s-balance-visible") !== "false");
const isSessionStateFromCacheReady = ref(false);
const webViewRestore = resolveWebViewRestore();

function resolveWebViewRestore(): NgRestoreSession | null {
    // TODO: (XBK_END) swap window.player for environment.user
    if (window?.player?.forceSessionRestore && window?.player?.id && window?.player?.sessionToken) {
        return { playerId: window.player.id, sessionToken: window.player.sessionToken };
    }

    return getPendingWebViewRestore();
}

/**
 * in hallers
 */
const balanceNumeric = computed(() => (balance.value?.Balance ? parseInt(balance.value.Balance) : null));
const isLoggedIn = computed(() => !!session.value?.SessionToken);

const { isDocumentVisible } = usePageVisibility();
const loginDurationMilliseconds = ref(0);
const refreshInterval = ref<number | null>(null);
const loginDurationFormatted = computed(() =>
    DateTimeUtils.formatDuration(Math.max(0, loginDurationMilliseconds.value))
);

export const useNgSession = () => ({
    session,
    balance,
    balanceNumeric,
    showBalance,
    isLoggedIn,
    logonDate,
    loginDurationMilliseconds,
    loginDurationFormatted,
    isSessionStateFromCacheReady,
    init,
    restoreFromWebView,
    refreshBalance,
    refreshSession,
    hasEnoughMoney,
    getMissingMoney,
    clearSession,
    showRegistrationOrLogin,
    restoreSessionFromApi,
});

function clearSession() {
    if (!webViewRestore || isSessionStateFromCacheReady.value) {
        clearPendingWebViewRestore();
    }
    session.value = null;
}

function init() {
    if (webViewRestore) {
        persistPendingWebViewRestore(webViewRestore);
        session.value = {
            SessionToken: webViewRestore.sessionToken,
            PlayerID: webViewRestore.playerId,
            PlayerName: "",
        };
    } else {
        initFromCookies();
    }

    setupWatchers();
}

async function restoreFromWebView() {
    if (!webViewRestore) return;

    if (isLoggedIn.value) {
        // logout previously logged in player and restore new session
        if (session.value?.PlayerID === webViewRestore.playerId) {
            clearPendingWebViewRestore();
            isSessionStateFromCacheReady.value = true;
            return;
        }
        await logout();
    }

    await restoreSessionPassedFromBackend();
    isSessionStateFromCacheReady.value = true;
}

function restoreSessionFromApi(sessionRestore: NgRestoreSession) {
    return new Promise<void>((res) => {
        watch(
            isLoggedIn,
            (newVal) => {
                if (newVal) {
                    res();
                }
            },
            { immediate: true }
        );
        ng_fw.restoreSession({
            isPostLoginPopupRequired: false,
            ...sessionRestore,
        });

        if (isLoggedIn.value) logLogin(ngSessionToContact(session.value!), session.value?.PlayerID);
    });
}

function restoreSessionPassedFromBackend() {
    return new Promise<void>((res) => {
        let notAbleToRestoreSessionTimeout = 0;

        // watch for login
        const stopWatch = watch(isLoggedIn, (isLoggedIn) => {
            if (!isLoggedIn) return;

            clearTimeout(notAbleToRestoreSessionTimeout);
            stopWatch();
            clearPendingWebViewRestore();
            logLogin(ngSessionToContact(session.value!), session.value?.PlayerID);
            res();
        });

        if (webViewRestore) {
            ng_fw.restoreSession({ isPostLoginPopupRequired: false, ...webViewRestore });
        }

        notAbleToRestoreSessionTimeout = window.setTimeout(() => {
            stopWatch();

            // The handoff is kept for the next page load, so alert only once it is given up on
            if (registerFailedWebViewRestore()) {
                showAlert({
                    title: "Přihlášení se nezdařilo",
                    subText: "Nepodařilo se přenést přihlášení z mobilní aplikace",
                });
            }
            res();
        }, 10_000);
    });
}

function initFromCookies() {
    const sessionToken = storage.getItem("SessionToken");
    if (sessionToken) {
        balance.value = {
            DisplayBalance: storage.getItem("s-balance"),
        };
        logonDate.value = parseInt(storage.getItem("s-logon-date")) || Date.now();
        session.value = {
            SessionToken: sessionToken,
            PlayerID: storage.getItem("PlayerID"),
            PlayerName: storage.getItem("s-player-name"),
        };
    }
    isSessionStateFromCacheReady.value = true;
}

async function refreshBalance(force = false) {
    if (!isLoggedIn.value) return;

    if (force) {
        await ng_fw.refreshBalance();
    }
    balance.value = ng_fw.getDisplayBalance();
}

function refreshSession() {
    session.value = ng_fw.getSessionDetails();
}

/**
 * Checks wether user has money for bet
 * @param requiredAmount value in Kc
 */
function hasEnoughMoney(requiredAmount: number) {
    return (balanceNumeric.value || 0) >= requiredAmount * 100;
}

/**
 * Gets missing money for bet
 * @param requiredAmount value in Kc
 */
function getMissingMoney(requiredAmount: number) {
    const sub = requiredAmount * 100 - balanceNumeric.value;
    return sub < 0 ? 0 : sub;
}

function setCookie(name: string, value: string, options?: Cookies.CookieAttributes) {
    if (value == null) {
        storage.removeItem(name);
    } else {
        storage.setItem(name, value, "essential", options);
    }
}

const startLoginDurationTimer = () => {
    loginDurationMilliseconds.value = logonDate.value ? Date.now() - logonDate.value : 0;
    refreshInterval.value = window.setInterval(() => {
        loginDurationMilliseconds.value += 1000;
    }, 1000);
};

function setupWatchers() {
    watchEffect(() => setCookie("s-balance-visible", showBalance.value as any, { expires: 365 }));
    watchEffect(() => setCookie("s-balance", balance.value?.DisplayBalance));
    watchEffect(() => setCookie("s-logon-date", logonDate.value as any));
    watchEffect(() => {
        setNgLocalStorageData();
        setCookie("PlayerID", session.value?.PlayerID, { sameSite: "strict" });
        setCookie("SessionToken", session.value?.SessionToken, { sameSite: "strict" });
        setCookie("s-player-name", session.value?.PlayerName);
        if (!session.value?.SessionToken) {
            balance.value = null;
            logonDate.value = null;
        } else if (session.value?.LoginTimeStamp) {
            logonDate.value = getNgTimeDuration(session.value.LoginTimeStamp, session.value.ServerTimeStamp);
        }
        setAuthenticatedBodyClass();
    });

    watch(
        [logonDate, isDocumentVisible],
        () => {
            clearInterval(refreshInterval.value);
            if (isLoggedIn.value) startLoginDurationTimer();
        },
        {
            immediate: true,
        }
    );

    watch(isLoggedIn, (newIsLoggedIn) => {
        if (!newIsLoggedIn) {
            useLocalStorage().removeItem(WECOMA_CONSTANTS.WecomaKeyStorage);
        }
    });
}

function setAuthenticatedBodyClass() {
    if (isLoggedIn.value) {
        document.body.classList.add("authenticated");
        document.body.classList.remove("unauthenticated");
    } else {
        document.body.classList.remove("authenticated");
        document.body.classList.add("unauthenticated");
    }
}

// We are trying to set session data for NG,
// because they cant do it themselves because of 3rd party cookies restriction
function setNgLocalStorageData() {
    const key = "NeoGames_120_USER_SESSION_CREDENTIALS";
    if (isLoggedIn.value) {
        useLocalStorage().setJson(
            key,
            {
                ISID: session.value.SessionToken,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                EXTERNAL_PLAYER_ID: session.value.PlayerID,
            },
            "essential"
        );
    } else {
        useLocalStorage().removeItem(key);
    }
}

async function showRegistrationOrLogin() {
    ng_fw.showLogin();
}

function logout() {
    return new Promise<void>((res) => {
        if (!isLoggedIn.value) {
            res();
            return;
        }
        const stopWatch = watch(isLoggedIn, (newVal) => {
            if (!newVal) {
                stopWatch();
                res();
            }
        });
        ng_fw.logout();
    });
}
